import { useEffect, useRef, useState, type ReactNode } from 'react'
import { TabBar, type TabBarItem } from '@/components/TabBar'
import { ClaudeApiKeySection, type ClaudeApiKeySectionProps } from './ClaudeApiKeySection'
import { CopilotApiKeySection, type CopilotApiKeySectionProps } from './CopilotApiKeySection'

type AgentProvider = 'claude' | 'copilot' | 'titan'

const PROVIDER_LABELS: Record<AgentProvider, string> = {
  claude: 'Claude Code',
  copilot: 'Copilot CLI',
  titan: 'Qubiz Titan',
}

interface AgentProviderTokenSectionProps {
  claude?: ClaudeApiKeySectionProps
  copilot?: CopilotApiKeySectionProps
  titan?: ReactNode
  titanHasKey?: boolean
  titanIsLoading?: boolean
  testIdPrefix?: string
}

export function AgentProviderTokenSection({
  claude,
  copilot,
  titan,
  titanHasKey = false,
  titanIsLoading = false,
  testIdPrefix = 'ai-provider',
}: AgentProviderTokenSectionProps) {
  const available: AgentProvider[] = [
    ...(claude ? (['claude'] as const) : []),
    ...(copilot ? (['copilot'] as const) : []),
    ...(titan ? (['titan'] as const) : []),
  ]

  const [activeTab, setActiveTab] = useState<AgentProvider>(available[0] ?? 'claude')
  const initialized = useRef(false)
  const userSelected = useRef(false)

  const handleTabChange = (tab: AgentProvider) => {
    userSelected.current = true
    setActiveTab(tab)
  }

  useEffect(() => {
    if (initialized.current) return
    if (available.length < 2) return

    const claudeLoading = claude?.isLoading ?? false
    const copilotLoading = copilot?.isLoading ?? false
    if (claudeLoading || copilotLoading || titanIsLoading) return

    initialized.current = true

    // If the user already clicked a tab, don't override their choice.
    if (userSelected.current) return

    if (copilot?.hasKey) {
      setActiveTab('copilot')
    } else if (claude?.hasKey) {
      setActiveTab('claude')
    } else if (titanHasKey) {
      setActiveTab('titan')
    } else if (claude) {
      setActiveTab('claude')
    }
  }, [available.length, claude, claude?.isLoading, claude?.hasKey, copilot, copilot?.isLoading, copilot?.hasKey, titanHasKey, titanIsLoading])

  if (available.length === 0) return null

  if (available.length === 1) {
    if (claude) return <ClaudeApiKeySection {...claude} />
    if (copilot) return <CopilotApiKeySection {...copilot} />
    return <>{titan}</>
  }

  const tabs: TabBarItem<AgentProvider>[] = available.map(key => ({ key, label: PROVIDER_LABELS[key] }))

  return (
    <section data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-xl font-bold mb-4">AI Provider</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Configure credentials for each AI provider. Providers are independent — configuring one leaves the
        others in place, so you can pick which provider runs a given workflow.
      </p>

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="mb-6"
      />

      <div role="tabpanel" data-testid={`${testIdPrefix}-${activeTab}-panel`}>
        {activeTab === 'claude' && claude && <ClaudeApiKeySection {...claude} />}
        {activeTab === 'copilot' && copilot && <CopilotApiKeySection {...copilot} />}
        {activeTab === 'titan' && titan}
      </div>
    </section>
  )
}
